import { addDays, addMonths, format, isAfter, isBefore, parseISO, startOfToday } from "date-fns";
import { t } from "i18next";

import { useStaffMembersAbsences } from "~/modules/absence/api/absence/absenceQueries.ts";
import { AbsenceTypeId } from "~/modules/absence/api/absence/absenceTypes.ts";
import { useAllAbsenceTypes } from "~/modules/absence/api/absenceType/absenceTypeQueries.ts";
import { useHolidaysByYear } from "~/modules/absence/api/holiday/holidayQueries.ts";
import { useDashboardUser } from "~/modules/dashboard/hooks/useDashboardUser.tsx";
import { useUsersActiveStaffings } from "~/modules/project/api/staffing/staffingQueries.ts";

const UPCOMING_WINDOW_DAYS = 14;

// The order of the kinds is the order of the events on one day.
export type UpcomingEventKind = "holiday" | "internalEvent" | "absence" | "staffing";
const KIND_ORDER: UpcomingEventKind[] = ["holiday", "internalEvent", "absence", "staffing"];

export type UpcomingEvent = {
	key: string;
	kind: UpcomingEventKind;
	/** The first day in the window: today for an absence that started earlier. */
	date: Date;
	/** Set for an absence of more than one day. */
	endDate: Date | null;
	/** The absence started before today. */
	startedEarlier: boolean;
	text: string;
};

function isInWindow(date: Date, windowStart: Date, windowEnd: Date) {
	return !isBefore(date, windowStart) && !isAfter(date, windowEnd);
}

/**
 * Builds the events of the next 14 days for the own dashboard. The list holds public holidays, own absences
 * and the start and end of own staffings. The backend has no event model, so an own absence of the type
 * "Internes Event" stands for the company event. The list reads own absences only: the company-wide read
 * `GET absences` needs the permission to view absences and returns the sick days of all staff.
 */
export function useUpcomingEvents(isEnabled: boolean) {
	const user = useDashboardUser();
	const windowStart = startOfToday();
	const windowEnd = addDays(windowStart, UPCOMING_WINDOW_DAYS - 1);

	// A window past New Year needs the holidays of the next year.
	const startYear = windowStart.getFullYear();
	const endYear = windowEnd.getFullYear();
	const userId = isEnabled ? user?.id ?? "" : "";
	const { data: holidaysOfStartYear } = useHolidaysByYear(isEnabled ? String(startYear) : "");
	const { data: holidaysOfEndYear } = useHolidaysByYear(isEnabled && endYear !== startYear ? String(endYear) : "");

	const { data: absences } = useStaffMembersAbsences(isEnabled ? user?.staffMemberId ?? "" : "");
	const { data: absenceTypes } = useAllAbsenceTypes();

	// A staffing read returns one month. A window into the next month needs that month for its starts and ends.
	const { data: currentStaffings } = useUsersActiveStaffings(userId);
	const spillsIntoNextMonth = windowEnd.getMonth() !== windowStart.getMonth();
	const { data: nextStaffings } = useUsersActiveStaffings(
		userId,
		spillsIntoNextMonth ? format(addMonths(windowStart, 1), "yyyy-MM") : "",
	);

	if (!isEnabled) return [];

	const events = new Map<string, UpcomingEvent>();

	for (const holiday of [...(holidaysOfStartYear ?? []), ...(holidaysOfEndYear ?? [])]) {
		const date = parseISO(holiday.date);
		if (!isInWindow(date, windowStart, windowEnd)) continue;
		events.set(`holiday-${holiday.id}`, {
			key: `holiday-${holiday.id}`,
			kind: "holiday",
			date,
			endDate: null,
			startedEarlier: false,
			text: holiday.displayName,
		});
	}

	for (const absence of absences ?? []) {
		// A sick absence is private, so it never shows.
		if (absence.absenceTypeId === AbsenceTypeId.Sick) continue;
		const start = parseISO(absence.startDate);
		const end = parseISO(absence.endDate);
		if (isAfter(start, windowEnd) || isBefore(end, windowStart)) continue;
		const typeName = absenceTypes?.find((type) => type.id === absence.absenceTypeId)?.name;
		if (!typeName) continue;
		const startedEarlier = isBefore(start, windowStart);
		events.set(`absence-${absence.id}`, {
			key: `absence-${absence.id}`,
			kind: absence.absenceTypeId === AbsenceTypeId.InternalEvent ? "internalEvent" : "absence",
			date: startedEarlier ? windowStart : start,
			endDate: end.getTime() === start.getTime() ? null : end,
			startedEarlier,
			text: t(`entities:absenceType.${typeName}` as unknown as TemplateStringsArray),
		});
	}

	for (const data of [currentStaffings, nextStaffings]) {
		for (const project of data?.projects ?? []) {
			for (const phase of project.projectPhases) {
				for (const order of phase.orders) {
					for (const staffing of order.staffings) {
						for (const [kind, label, dateString] of [
							["start", "Start", staffing.startDate],
							["end", "Ende", staffing.endDate],
						] as const) {
							if (!dateString) continue;
							const date = parseISO(dateString);
							if (!isInWindow(date, windowStart, windowEnd)) continue;
							const key = `staffing-${staffing.id}-${kind}`;
							events.set(key, {
								key,
								kind: "staffing",
								date,
								endDate: null,
								startedEarlier: false,
								text: `${label}: ${project.title}`,
							});
						}
					}
				}
			}
		}
	}

	return [...events.values()].sort((a, b) =>
		a.date.getTime() - b.date.getTime() || KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind));
}
